import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Activity, FileText } from "lucide-react";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { MyContract } from "../../types/myContract";
import { createDataTablePagination } from "./DataTablePagination";
import { DEFAULT_LIST_PAGE_SIZE } from "../../constants/listPagination";

const myContractsTablePagination = createDataTablePagination({
  rowsPerPageOptions: [10, 20, 30]
});

type MyContractsTableProps = {
  readonly contracts: MyContract[];
  readonly onOpenActivityStream?: (row: MyContract) => void;
  /** API-driven paging: Next/Previous fetch new rows */
  readonly paginationServer: boolean;
  readonly totalRows: number;
  readonly page: number;
  readonly perPage: number;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (nextPerPage: number, page: number) => void;
  readonly loading: boolean;
};

// const statusConfig: Record<ContractStatus, { bg: string; text: string }> = {
//   Pending: { bg: "bg-orange-50 border-orange-200", text: "text-orange-600" },
//   "Not yet Reviewed": {
//     bg: "bg-blue-50 border-blue-200",
//     text: "text-blue-600"
//   },
//   "In Review": { bg: "bg-amber-50 border-amber-200", text: "text-amber-600" },
//   Completed: {
//     bg: "bg-emerald-50 border-emerald-200",
//     text: "text-emerald-600"
//   }
// };

const FileIcon = ({ name }: { readonly name: string }) => {
  const isPdf = name.toLowerCase().endsWith(".pdf");
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${isPdf ? "bg-red-50 text-red-600" : "bg-sky-50 text-sky-600"
        }`}
      aria-hidden
    >
      <FileText className="h-4 w-4" strokeWidth={1.75} />
    </span>
  );
};

// const StatusBadge = ({ status }: { readonly status: ContractStatus }) => {
//   const config = statusConfig[status];
//   return (
//     <span
//       className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}
//     >
//       {status}
//     </span>
//   );
// };

const RowActions = ({
  contract,
  onOpenActivityStream
}: {
  readonly contract: MyContract;
  readonly onOpenActivityStream?: (row: MyContract) => void;
}) => (
  <div className="flex items-center justify-end">
    <button
      type="button"
      onClick={() => onOpenActivityStream?.(contract)}
      disabled={!onOpenActivityStream}
      title="Activity Stream"
      className="flex h-8 w-8 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
      aria-label={`Activity Stream for ${contract.documentName}`}
    >
      <Activity className="h-4 w-4" strokeWidth={1.75} aria-hidden />
    </button>
  </div>
);

export const MyContractsTable = ({
  contracts,
  onOpenActivityStream,
  paginationServer,
  totalRows,
  page,
  perPage,
  onChangePage,
  onChangeRowsPerPage,
  loading
}: MyContractsTableProps) => {
  const columns: TableColumn<MyContract>[] = useMemo(
    () => [
      {
        name: "Document Name",
        selector: (row) => row.documentName,
        sortable: !paginationServer,
        grow: 2,
        cell: (row) => (
          <div className="flex items-center gap-2 line-clamp-1" title={row.documentName}>
            <FileIcon name={row.documentName} />
            <span className="truncate font-medium text-gray-900">
              {row.documentName}
            </span>
          </div>
        ),
        width:"500px"
      },
      {
        name: "Contract Type",
        selector: (row) => row.contractType,
        sortable: !paginationServer,
        width: "200px"
      },
      {
        name: "Contract Origination",
        selector: (row) => row.contractOrigination,
        sortable: !paginationServer,
        width: "200px"
      },
      {
        name: "Business Unit",
        selector: (row) => row.businessUnit,
        sortable: !paginationServer,
        width: "200px"
      },
      {
        name: "Status",
        selector: (row) => row.status,
        sortable: !paginationServer,
        width: "150px",
        cell: (row) => row.status
      },
      {
        name: "Modified By",
        selector: (row) => row.modifiedBy,
        sortable: !paginationServer,
        width: "250px",
        cell: (row) => (
          <div>
            <p className="font-medium text-gray-900">{row.modifiedBy}</p>
            <p className="text-xs text-gray-500">{row.modifiedByEmail}</p>
          </div>
        )
      },
      {
        name: "Modified Date",
        selector: (row) => row.modifiedDate,
        sortable: !paginationServer,
        width: "130px",
        cell: (row) => (
          <span className="text-sm text-gray-500">{row.modifiedDate}</span>
        )
      },
      {
        name: "Action",
        cell: (row) => (
          <RowActions
            contract={row}
            onOpenActivityStream={onOpenActivityStream}
          />
        ),
        width: "100px",
        right: true
      }
    ],
    [onOpenActivityStream, paginationServer]
  );

  const pageSizeOptions = [10, 20, 30];
  const serverProps = paginationServer
    ? {
        paginationServer: true as const,
        paginationTotalRows: totalRows,
        paginationDefaultPage: page,
        paginationPerPage: perPage,
        paginationRowsPerPageOptions: pageSizeOptions,
        onChangePage,
        onChangeRowsPerPage
      }
    : {
        paginationPerPage: DEFAULT_LIST_PAGE_SIZE,
        paginationRowsPerPageOptions: pageSizeOptions
      };

  return (
    <DataTable
      columns={columns}
      data={contracts}
      customStyles={dataTableCustomStyles}
      striped={false}
      pagination
      {...serverProps}
      paginationComponent={myContractsTablePagination}
      progressPending={loading}
      noDataComponent={
        <div className="p-12 text-center">
          <p className="text-sm text-gray-500">
            No contracts found matching your search.
          </p>
        </div>
      }
      highlightOnHover
      responsive
    />
  );
};
